// pages/integrations/WebRTC.tsx
import React, { useState } from 'react';
import { Phone, Copy, Check, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { Entreprise } from '../../../../types/features/Entreprise';
import useAuth from '../../../../hooks/features/auth/useAuth';

export const WebRTCIntegrationPage: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const { user } = useAuth();
  const entreprise = user?.profile as Entreprise;
  const originUrl = window.location.origin;
  const slug = entreprise?.name
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || '';

  const config = {
    entrepriseId: entreprise?.id || '',
    callLink: `${originUrl}/call/${slug}/${entreprise?.id || ''}`,
  };

  const { callLink } = config;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-6">
        <div className="p-2 bg-primary/10 rounded-xl">
          <Phone size={40} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Appels WebRTC</h2>
          <p className="text-sm text-slate-600">
            Vos clients peuvent vous appeler directement depuis leur navigateur, sans téléphone
          </p>
        </div>
      </div>

      {/* Lien d'appel */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <LinkIcon size={18} className="text-primary" />
          Lien d'appel
        </h3>

        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1 w-full">
              <div className="text-xs text-slate-500 mb-1">Partagez ce lien avec vos clients</div>
              <code className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-700 truncate">
                {callLink}
              </code>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => copyToClipboard(callLink, 'link')}
                className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors flex-1 sm:flex-none justify-center"
              >
                {copied === 'link' ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
                <span className="text-sm font-medium text-slate-600">
                  {copied === 'link' ? 'Copié !' : 'Copier'}
                </span>
              </button>
              <a
                href={callLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex-1 sm:flex-none justify-center"
              >
                <ExternalLink size={18} />
                <span className="text-sm font-medium">Tester</span>
              </a>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-3">
          Vos clients cliquent sur le lien, entrent leur email et vous appellent directement
        </p>
      </div>

      {/* Aperçu */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Aperçu du bouton d'appel</h3>
        <div className="p-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 flex flex-col sm:flex-row items-center gap-4 justify-center">
          <a
            href={callLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-white px-8 py-3.5 rounded-lg hover:bg-primary/90 transition flex items-center gap-2 shadow-lg text-base"
          >
            <Phone size={20} />
            Nous appeler
          </a>
          <span className="text-sm text-slate-400">Les clients cliquent et l'appel démarre</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Comment ça marche ?</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">1.</span>
            Copiez le lien d'appel ci-dessus
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">2.</span>
            Partagez-le avec vos clients (email, WhatsApp, QR code, etc.)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">3.</span>
            Le client clique, entre son email et vous appelle instantanément
          </li>
        </ul>
      </div>
    </div>
  );
};